/**
 * Migration 0102 — public `/household/join/:token` page.
 *
 * Lands here from the `customer.household_invite` email. NO auth gate: the
 * page first shows who invited them (public token lookup), then either
 *   - signed in with the invited email → "Accept" → POST /accept, or
 *   - not signed in → MagicLinkForm with `callbackURL` = this page, so the
 *     magic-link round trip returns them here to accept.
 *
 * States: loading → ready | accepted | already_member | expired | revoked |
 * invalid | already_in_household | email_mismatch | error.
 */
import { type CSSProperties, type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { MagicLinkForm } from '../components/MagicLinkForm';
import { CustomerApiError, customerApi } from '../customer_api';
import { useCustomerAuth } from '../useCustomerAuth';

interface InviteLookup {
  accountName: string;
  householdName: string;
  invitedEmail: string | null;
  permissions: {
    canBook: boolean;
    canPay: boolean;
    canEditPets: boolean;
    receivesNotifications: boolean;
  };
  expiresAt: string;
  alreadyAccepted: boolean;
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: InviteLookup }
  | { kind: 'accepted'; alreadyAccepted: boolean }
  | { kind: 'sent'; email: string }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'invalid' }
  | { kind: 'already_in_household'; message: string }
  | { kind: 'email_mismatch'; message: string }
  | { kind: 'error'; message: string };

function stateFromError(err: unknown): PageState {
  if (err instanceof CustomerApiError) {
    const body = (err.body as { error?: string; message?: string } | null) ?? null;
    const code = body?.error;
    if (code === 'link_expired') return { kind: 'expired' };
    if (code === 'invite_revoked') return { kind: 'revoked' };
    if (err.status === 404 || code === 'invalid_link') return { kind: 'invalid' };
    if (code === 'already_in_household') {
      return {
        kind: 'already_in_household',
        message: body?.message ?? 'Your login is already in another household.',
      };
    }
    if (code === 'email_mismatch') {
      return {
        kind: 'email_mismatch',
        message: body?.message ?? 'Sign in with the invited email to accept.',
      };
    }
    if (code === 'already_accepted')
      return { kind: 'error', message: 'This invitation has already been used by someone else.' };
    if (err.status === 429)
      return { kind: 'error', message: 'Too many attempts — please wait a minute and try again.' };
    return { kind: 'error', message: `Something went wrong (${err.status}).` };
  }
  return { kind: 'error', message: 'Something went wrong. Please try again.' };
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: '440px',
  background: 'var(--surface, white)',
  padding: '2rem 1.75rem',
  borderRadius: '14px',
  boxShadow: 'inset 0 0 0 1px var(--border, oklch(0.9 0.005 80))',
};

const primaryButton: CSSProperties = {
  width: '100%',
  marginTop: '1.25rem',
  padding: '0.7rem',
  borderRadius: '8px',
  background: 'var(--brand-primary, #004AAD)',
  color: 'white',
  fontWeight: 500,
  fontSize: '0.95rem',
  border: 'none',
  cursor: 'pointer',
};

function Muted({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        color: 'var(--muted-foreground, #6b6b6b)',
        fontSize: '0.875rem',
        marginTop: '0.5rem',
      }}
    >
      {children}
    </p>
  );
}

export function HouseholdJoin() {
  const { token = '' } = useParams<{ token: string }>();
  const { loading, user, refresh } = useCustomerAuth();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    customerApi<InviteLookup>(`/api/customer/household/invites/${encodeURIComponent(token)}`)
      .then((invite) => {
        if (!cancelled) setState({ kind: 'ready', invite });
      })
      .catch((err) => {
        if (!cancelled) setState(stateFromError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = useCallback(async () => {
    setAccepting(true);
    try {
      const res = await customerApi<{ ok: boolean; alreadyAccepted: boolean }>(
        `/api/customer/household/invites/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      setState({ kind: 'accepted', alreadyAccepted: res.alreadyAccepted });
      void refresh();
    } catch (err) {
      setState(stateFromError(err));
    } finally {
      setAccepting(false);
    }
  }, [token, refresh]);

  const returnTo = `/household/join/${token}`;

  let body: ReactNode;
  switch (state.kind) {
    case 'loading':
      body = <Muted>Checking your invitation…</Muted>;
      break;
    case 'invalid':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>This link isn't valid</h1>
          <Muted>
            It may have been copied incorrectly. Ask the person who invited you to send it again.
          </Muted>
        </>
      );
      break;
    case 'expired':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>This invitation has expired</h1>
          <Muted>
            Invitations last 7 days. Ask the person who invited you to send a fresh one.
          </Muted>
        </>
      );
      break;
    case 'revoked':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>This invitation was withdrawn</h1>
          <Muted>
            The household's primary contact cancelled it. Ask them to invite you again if that was a
            mistake.
          </Muted>
        </>
      );
      break;
    case 'already_in_household':
    case 'email_mismatch':
    case 'error':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>
            {state.kind === 'email_mismatch'
              ? 'Wrong email address'
              : state.kind === 'already_in_household'
                ? "You're already in a household"
                : 'Something went wrong'}
          </h1>
          <p role="alert" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            {state.message}
          </p>
          {state.kind === 'already_in_household' ? (
            <p style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
              <Link to="/account/household">Open your Household page</Link>
            </p>
          ) : null}
        </>
      );
      break;
    case 'accepted':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>
            {state.alreadyAccepted ? "You're already a member" : "You've joined the household"}
          </h1>
          <Muted>You can now see the household's pets and bookings.</Muted>
          <Link
            to="/account"
            style={{
              ...primaryButton,
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            Go to your account
          </Link>
        </>
      );
      break;
    case 'sent':
      body = (
        <>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500 }}>Check your email</h1>
          <Muted>
            We've sent a sign-in link to {state.email}. Open it on this device and you'll come
            straight back here to accept.
          </Muted>
        </>
      );
      break;
    case 'ready': {
      const { invite } = state;
      body = (
        <>
          <p
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'var(--muted-foreground)',
            }}
          >
            {invite.accountName}
          </p>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500, marginTop: '0.35rem' }}>
            Join the {invite.householdName || 'household'} household
          </h1>
          <Muted>
            You've been invited to share this household's pets and bookings
            {invite.invitedEmail ? ` as ${invite.invitedEmail}` : ''}.
          </Muted>
          <ul
            style={{
              margin: '0.9rem 0 0',
              paddingLeft: '1.1rem',
              fontSize: '0.85rem',
              color: 'var(--muted-foreground)',
            }}
          >
            <li>
              {invite.permissions.canBook ? 'You can make bookings' : "You can't make bookings yet"}
            </li>
            <li>
              {invite.permissions.canPay
                ? 'You can pay for bookings'
                : "You can't pay for bookings yet"}
            </li>
            <li>
              {invite.permissions.canEditPets
                ? 'You can update pet details'
                : "You can't update pet details yet"}
            </li>
          </ul>
          {loading ? (
            <Muted>Loading…</Muted>
          ) : user ? (
            <>
              <Muted>Signed in as {user.email}.</Muted>
              <button
                type="button"
                style={primaryButton}
                disabled={accepting}
                onClick={() => void accept()}
              >
                {accepting ? 'Joining…' : 'Accept invitation'}
              </button>
            </>
          ) : (
            <div style={{ marginTop: '1.25rem' }}>
              <Muted>
                Sign in with the invited email to accept. We'll email you a one-time link.
              </Muted>
              <div style={{ marginTop: '0.75rem' }}>
                <MagicLinkForm
                  callbackURL={returnTo}
                  submitLabel="Email me a sign-in link"
                  inputId="household-join-email"
                  onSent={(email) => setState({ kind: 'sent', email })}
                />
              </div>
            </div>
          )}
        </>
      );
      break;
    }
  }

  return (
    <BrandedShell>
      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          background: 'var(--background, #fafaf7)',
        }}
      >
        <div style={card}>{body}</div>
      </main>
    </BrandedShell>
  );
}
